import { useState, useEffect, useCallback } from "react"
import { getAuth, sendEmailVerification } from "firebase/auth"
import { Typography, Button, Alert, Card, Stack, Container, CircularProgress } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { LogoutOutlined, MarkEmailRead } from "@mui/icons-material"
import LogoutConfirmDialog from "@/shared/components/LogoutConfirmDialog"

const VerifyEmail = () => {
  const auth = getAuth()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)
  const navigate = useNavigate()

  const resendVerification = async () => {
    if (auth.currentUser) {
      setLoading(true)
      try {
        const code = { url: "https://listyourproduct.com", handleCodeInApp: true }
        await sendEmailVerification(auth.currentUser, code)
        setMessage("Verification email sent! Check your inbox.")
      } catch (error) {
        setMessage("Failed to send verification email. Please try again.")
      }
      setLoading(false)
    }
  }

  const openLogoutConfirm = useCallback(() => {
    setLogoutConfirmOpen(true)
  }, [])

  const closeLogoutConfirm = useCallback(() => {
    setLogoutConfirmOpen(false)
  }, [])

  const onLogout = useCallback(() => {
    auth.signOut().then(() => {
      navigate("/")
    })
  }, [auth, navigate])

  useEffect(() => {
    if (auth.currentUser?.emailVerified) {
      setMessage("Your email is already verified!")
    }
  }, [auth.currentUser])

  return (
    <Container>
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Stack
          component={Card}
          alignItems="center"
          justifyContent="center"
          variant="outlined"
          p={6}
          sx={{ textAlign: "center" }}>
          <MarkEmailRead fontSize="inherit" style={{ fontSize: "100px" }} color="warning" />
          <Typography variant="h5" gutterBottom>
            Verify your email address
          </Typography>
          <Typography variant="body1">
            A verification email has been sent to <strong>{auth.currentUser?.email}</strong>.<br />
            Please check your inbox and confirm your email.
            <br />
            If you don&apos;t see it, check your junk or spam folder.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            If you don't see the email, check your spam folder or click the button below to resend. If you cannot find
            it, please check your junk or spam folder.
          </Alert>

          {message && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}

          {!auth.currentUser?.emailVerified && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
              onClick={resendVerification}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
              Resend Email
            </Button>
          )}

          <Button color="primary" sx={{ mt: 3 }} onClick={openLogoutConfirm} endIcon={<LogoutOutlined />}>
            Logout
          </Button>
        </Stack>
      </Stack>
      <LogoutConfirmDialog open={logoutConfirmOpen} onClose={closeLogoutConfirm} onConfirm={onLogout} />
    </Container>
  )
}

export default VerifyEmail
